import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Shield, Check, AlertCircle } from 'lucide-react';
import './SecureBankAccountDisplay.css';

const SecureBankAccountDisplay = ({
  accountNumber,
  accountName,
  bankName,
  verified = false,
  onVerify,
  onConfirmPassword,
  showActions = true
}) => {
  const [showFull, setShowFull] = useState(false);
  const [timer, setTimer] = useState(null);

  // Mask account number
  const maskAccountNumber = (number) => {
    if (!number) return '****';
    const last4 = number.slice(-4);
    return `****${last4}`;
  };

  // Handle view full account
  const handleViewFull = async () => {
    if (!onConfirmPassword) {
      setShowFull(true);
      startAutoHideTimer();
      return;
    }

    // Request password confirmation
    const confirmed = await onConfirmPassword();
    if (confirmed) {
      setShowFull(true);
      startAutoHideTimer();
    }
  };

  // Auto-hide after 30 seconds
  const startAutoHideTimer = () => {
    // Clear existing timer
    if (timer) {
      clearTimeout(timer);
    }

    // Set new timer
    const newTimer = setTimeout(() => {
      setShowFull(false);
    }, 30000); // 30 seconds

    setTimer(newTimer);
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [timer]);

  // Manual hide
  const handleHide = () => {
    setShowFull(false);
    if (timer) {
      clearTimeout(timer);
      setTimer(null);
    }
  };

  if (!accountNumber) {
    return (
      <div className="secure-bank-account no-account">
        <div className="no-account-icon">
          <AlertCircle size={40} />
        </div>
        <div className="no-account-text">
          <h4>No Bank Account Linked</h4>
          <p>Add your bank account to receive settlements</p>
        </div>
        {showActions && onVerify && (
          <button className="add-account-btn" onClick={onVerify}>
            Add Bank Account
          </button>
        )}
      </div>
    );
  }

  return (
    <div className={`secure-bank-account ${verified ? 'verified' : 'unverified'}`}>
      <div className="account-header">
        <div className="account-icon">
          <Shield size={24} />
        </div>
        <div className="account-info">
          <div className="account-name">{accountName || 'Account Name Not Available'}</div>
          <div className="bank-name">{bankName || 'Bank Name'}</div>
        </div>
        <div className="verification-badge">
          {verified ? (
            <>
              <Check size={16} />
              <span>Verified</span>
            </>
          ) : (
            <>
              <AlertCircle size={16} />
              <span>Unverified</span>
            </>
          )}
        </div>
      </div>

      <div className="account-number-section">
        <div className="account-label">Account Number</div>
        <div className="account-number-display">
          <span className="account-number">
            {showFull ? accountNumber : maskAccountNumber(accountNumber)}
          </span>
          {showActions && (
            <button
              className="toggle-visibility-btn"
              onClick={showFull ? handleHide : handleViewFull}
              title={showFull ? 'Hide account number' : 'View full account number'}
            >
              {showFull ? <EyeOff size={18} /> : <Eye size={18} />}
              <span>{showFull ? 'Hide' : 'View'}</span>
            </button>
          )}
        </div>
        {showFull && (
          <div className="auto-hide-notice">
            Will auto-hide in 30 seconds
          </div>
        )}
      </div>

      {!verified && showActions && onVerify && (
        <div className="verify-section">
          <div className="verify-notice">
            <AlertCircle size={16} />
            <span>This account has not been verified yet</span>
          </div>
          <button className="verify-btn" onClick={onVerify}>
            Verify Account
          </button>
        </div>
      )}
    </div>
  );
};

export default SecureBankAccountDisplay;
